import { trackerMaanden } from './tracker.js';
import { monthLabel } from './dates.js';
import { geredenKm, kiesPeriode, kostenTotaal, verbruik } from './persoonkosten.js';
/**
 * De kengetallen boven de persoonspagina, in dezelfde vorm als de tegels op het
 * vlootdashboard: een waarde, een sparkline en het verschil met de vorige
 * periode. Door er `Tracker`-objecten van te maken kan `TrackerTegel` ze
 * onveranderd tonen.
 *
 * **De tegels passen zich aan de persoon aan.** Wie nooit laadt krijgt geen
 * laadtegels en wie alleen elektrisch rijdt geen brandstoftegels: een tegel
 * zonder cijfers is geen informatie maar ruis. Datzelfde doet het dashboard met
 * lege trackers.
 *
 * De reeks achter de sparkline is altijd maandelijks, ook als je een jaar
 * bekijkt. Een jaartegel met twee punten is geen lijn, en de vraag "hoe liep het"
 * beantwoord je met maanden.
 */
/** Hoeveel maanden er achter de sparkline zitten; gelijk aan het dashboard. */
const SPARKLINE_MAANDEN = 6;
/** Eén punt per maand, met de waarde die de tegel toont. */
function reeksVan(regels, maanden, waardeVan) {
    return maanden.map((maand) => {
        const inMaand = regels.filter((l) => l.maand === maand);
        const waarde = inMaand.length ? waardeVan(inMaand) : null;
        return {
            maand,
            label: monthLabel(maand),
            waarde: waarde ?? 0,
            autos: 0,
            // Geen transacties is iets anders dan nul euro: de sparkline springt over
            // die maand heen in plaats van naar de bodem te duiken.
            ...(waarde == null ? { leeg: true } : {}),
        };
    });
}
/**
 * De tegel toont het laatste punt van zijn reeks en zet dat af tegen het punt
 * ervoor. Bij een gekozen maand valt dat samen met de maandreeks. Bij "dit jaar"
 * of "alles" niet, want dan is de waarde een optelsom over meerdere maanden;
 * daar plakken we de gekozen periode en de vorige als twee extra punten achter
 * de maandreeks, zodat de sparkline het verloop blijft tonen en het verschil
 * onder de tegel over de juiste periodes gaat.
 */
function metPeriodePunten(reeks, huidig, vorig) {
    const punt = (label, waarde) => ({
        maand: label,
        label,
        waarde: waarde ?? 0,
        autos: 0,
        ...(waarde == null ? { leeg: true } : {}),
    });
    const staart = vorig ? [punt(vorig.label, vorig.waarde)] : [];
    return [...reeks, ...staart, punt(huidig.label, huidig.waarde)];
}
export function buildPersoonKengetallen({ regels, filter, dagGrens, laatsteMaand, }) {
    if (!regels.length)
        return [];
    const maandenMetData = [...new Set(regels.map((l) => l.maand))].sort();
    const eind = laatsteMaand ??
        (typeof filter === 'string' && filter !== 'YTD'
            ? filter
            : (maandenMetData[maandenMetData.length - 1] ?? ''));
    if (!eind)
        return [];
    const maanden = trackerMaanden(eind, SPARKLINE_MAANDEN);
    const periode = kiesPeriode(regels, filter, dagGrens);
    const perMaandFilter = filter !== null && filter !== 'YTD';
    /**
     * Bouwt één tegel. Bij een gekozen maand is de maandreeks al precies wat de
     * tegel nodig heeft; bij een jaar of bij alles komen de periodetotalen erachter.
     */
    const maak = (key, label, sub, eenheid, waardeVan, kleur = 'kosten') => {
        const nu = waardeVan([...periode.regels]);
        if (nu == null)
            return null;
        const reeks = reeksVan(regels, maanden, waardeVan);
        return {
            key,
            label,
            sub,
            kleur,
            perAuto: false,
            eenheid,
            reeks: perMaandFilter
                ? reeks
                : metPeriodePunten(reeks, { label: periode.label, waarde: nu }, periode.vorigeLabel
                    ? { label: periode.vorigeLabel, waarde: waardeVan([...periode.vorige]) }
                    : null),
        };
    };
    const tegels = [
        maak('variabel', 'Totaal', 'Alles wat met de tankpas is betaald, excl. btw. Onderhoud staat op de auto en telt hier niet mee.', 'euro', (r) => (r.length ? kostenTotaal(r) : null)),
        maak('prijsLiter', 'Prijs per liter', 'Betaald bedrag gedeeld door het volume, dus een volle tank weegt zwaarder dan vijf liter.', 'euroPerEenheid', (r) => verbruik(r, 'LTR').prijs),
        maak('getankt', 'Getankt', 'Liters in deze periode.', 'liter', (r) => (verbruik(r, 'LTR').waarde > 0 ? verbruik(r, 'LTR').waarde : null), 'neutraal'),
        maak('prijsKwh', 'Prijs per kWh', 'Betaald bedrag gedeeld door de geladen kWh.', 'euroPerEenheid', (r) => verbruik(r, 'KWH').prijs),
        maak('geladen', 'Geladen', 'kWh in deze periode.', 'kwh', (r) => (verbruik(r, 'KWH').waarde > 0 ? verbruik(r, 'KWH').waarde : null), 'neutraal'),
        maak('perKm', 'Kosten per km', 'Variabele kosten gedeeld door de kilometers tussen de laagste en hoogste tellerstand in deze periode.', 'euroPerEenheid', (r) => {
            const km = geredenKm(r);
            return km && km > 0 ? kostenTotaal(r) / km : null;
        }),
    ];
    return tegels.filter((t) => t != null);
}
//# sourceMappingURL=persoonkengetallen.js.map